import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePageTitle } from '../../lib/usePageTitle';
import { formatDateShort } from '../../lib/dates';
import { AdminLayout } from './AdminLayout';
import { TextField } from '../../components/TextField';
import { Button } from '../../components/Button';
import { api } from '../../lib/api';

interface AuditEntry {
  id: string;
  action: string;
  entity: string;
  entityId: string | null;
  actor: string;
  league: string | null;
  reason: string | null;
  createdAt: string;
}

export function AdminAuditPage() {
  usePageTitle('Audit log — Amministrazione');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');

  const audit = useQuery({
    queryKey: ['admin', 'audit', filter],
    queryFn: () =>
      api<{ entries: AuditEntry[] }>(
        `/admin/audit${filter ? `?action=${encodeURIComponent(filter)}` : ''}`,
      ).then((r) => r.entries),
  });

  return (
    <AdminLayout title="Audit log">
      <form
        noValidate
        className="mt-6 flex max-w-md items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setFilter(search.trim());
        }}
      >
        <div className="flex-1">
          <TextField
            label="Filtra per azione"
            description="Esempio: auth.login, score.manual_adjust, matchday."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button type="submit" variant="secondary">
          Filtra
        </Button>
      </form>

      {audit.isLoading && (
        <p role="status" className="mt-6">Caricamento audit log…</p>
      )}

      {audit.data && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <caption className="visually-hidden">
              Registro delle operazioni: quando, chi, azione, lega e motivo
            </caption>
            <thead>
              <tr className="border-b-2 border-ink-300 dark:border-ink-600">
                <th scope="col" className="py-2 pr-3">Quando</th>
                <th scope="col" className="py-2 pr-3">Chi</th>
                <th scope="col" className="py-2 pr-3">Azione</th>
                <th scope="col" className="py-2 pr-3">Lega</th>
                <th scope="col" className="py-2">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {audit.data.map((e) => (
                <tr key={e.id} className="border-b border-ink-200 dark:border-ink-800">
                  <td className="py-2.5 pr-3 whitespace-nowrap">{formatDateShort(e.createdAt)}</td>
                  <td className="py-2.5 pr-3">{e.actor}</td>
                  <td className="py-2.5 pr-3 font-mono text-sm">{e.action}</td>
                  <td className="py-2.5 pr-3">{e.league ?? '—'}</td>
                  <td className="py-2.5">{e.reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
